/*
  Scroll-aware nav contrast (desktop only).

  The sidebar floats over the hero. White text reads well there but disappears
  against the white content below it. Rather than switch colour at a threshold —
  which pops — we stack two identical copies of the nav:

    base layer  : dark text, always painted
    light layer : white text, clipped to exactly the hero's visible area

  The clip edge is the hero's bottom in viewport coordinates, so as the page
  scrolls the boundary sweeps through the text and a letter can be part white
  and part dark. Only a CSS variable changes per frame, so the work stays on the
  compositor and no layout is triggered.
*/

const CONTRAST_QUERY = '(min-width: 990px)';

class SidebarScrollContrast {
  constructor(sidebar) {
    this.sidebar = sidebar;
    this.panel = sidebar.querySelector('.sidebar__panel');
    this.inner = sidebar.querySelector('.sidebar__inner');
    this.clone = null;
    this.frame = null;
    this.desktop = window.matchMedia(CONTRAST_QUERY);

    this.update = this.update.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);

    this.desktop.addEventListener('change', this.onBreakpoint);
    this.onBreakpoint();
  }

  /* The hero is whatever section comes first in the main content. */
  get heroBottom() {
    const banner = document.querySelector(
      '.banner, .slideshow, [id^="shopify-section"] .banner__media'
    );
    if (!banner) return 0;
    return banner.getBoundingClientRect().bottom;
  }

  enable() {
    if (this.clone || !this.inner || !this.panel) return;

    this.clone = this.inner.cloneNode(true);
    this.clone.classList.add('sidebar__inner--light');
    this.clone.setAttribute('aria-hidden', 'true');
    // Duplicated ids and controls must not be reachable or announced twice.
    this.clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    this.clone.querySelectorAll('a, button, summary, input').forEach((el) => {
      el.setAttribute('tabindex', '-1');
      el.setAttribute('aria-hidden', 'true');
    });
    this.panel.appendChild(this.clone);
    this.sidebar.classList.add('sidebar--scroll-contrast');

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  disable() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.clone) {
      this.clone.remove();
      this.clone = null;
    }
    this.sidebar.classList.remove('sidebar--scroll-contrast');
    this.sidebar.style.removeProperty('--sidebar-light-clip');
  }

  onBreakpoint() {
    this.desktop.matches ? this.enable() : this.disable();
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    // Clip the light layer to the region above the hero's bottom edge.
    const bottom = Math.max(0, this.heroBottom);
    this.sidebar.style.setProperty('--sidebar-light-clip', `${bottom}px`);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('sidebar-nav').forEach((el) => {
    if (el.dataset.overlay === 'scroll') new SidebarScrollContrast(el);
  });
});
